/**
 * Embed bridge: lets a hosting page sign this frontend in and out (F-FE-N210).
 *
 * The multi-brand terminal (terminal/) shows several tenants side by side,
 * each as an <iframe> of that tenant's own origin, and the cashier should sign
 * in ONCE. A token cannot be injected into another origin's storage, so the
 * shell hands the frame the *credentials* and the frame performs its own normal
 * same-origin /auth/login — the tenant validates, the tenant stores the JWT,
 * nothing about the session ever crosses the origin boundary. The shell only
 * learns "signed in or not" and the role.
 *
 * Trust model. The listener is installed ONLY when this document is framed
 * (`window.self !== window.top`), so every live tenant tab keeps exactly
 * today's behaviour. Who may frame a tenant is decided by the tenant's own CSP
 * `frame-ancestors` (nginx FRAME_ANCESTORS, strictly opt-in per tenant), and
 * only messages from `window.parent` — the framing page itself — are honoured.
 * A hostile page therefore cannot even load the frame, and a permitted shell
 * can do no more than a person typing at the login screen could.
 *
 * Protocol (v1). Frame → shell, posted to window.parent:
 *   { v:1, type:'bhookly:embed:ready', authenticated, role? }   on load, on hello, after login/logout
 *   { v:1, type:'bhookly:embed:login-result', ok, role?, error? }
 * Shell → frame:
 *   { v:1, type:'bhookly:embed:hello' }
 *   { v:1, type:'bhookly:embed:login', username, password }
 *   { v:1, type:'bhookly:embed:logout' }
 */

export const EMBED_PROTOCOL_VERSION = 1

export interface EmbedBridgeDeps {
  /** Perform the normal login; resolve with ok/error, never throw. */
  login(username: string, password: string): Promise<{ ok: boolean; role?: string; error?: string }>
  logout(): void
  isAuthenticated(): boolean
  role(): string | undefined
  /** Called after a successful login so the frame lands where its role belongs. */
  navigateHome(): void
}

export function isEmbedded(win: Window = window): boolean {
  try {
    return win.self !== win.top
  } catch {
    // A cross-origin top throws on access in some browsers; that is still "framed".
    return true
  }
}

type Incoming =
  | { v: 1; type: 'bhookly:embed:hello' }
  | { v: 1; type: 'bhookly:embed:login'; username: string; password: string }
  | { v: 1; type: 'bhookly:embed:logout' }

function isIncoming(d: unknown): d is Incoming {
  if (!d || typeof d !== 'object') return false
  const m = d as Record<string, unknown>
  if (m.v !== EMBED_PROTOCOL_VERSION || typeof m.type !== 'string') return false
  if (m.type === 'bhookly:embed:login') return typeof m.username === 'string' && typeof m.password === 'string'
  return m.type === 'bhookly:embed:hello' || m.type === 'bhookly:embed:logout'
}

/**
 * Install the listener. Returns the uninstall function. A no-op (returns a
 * no-op) when the document is not framed.
 */
export function installEmbedBridge(deps: EmbedBridgeDeps, win: Window = window): () => void {
  if (!isEmbedded(win)) return () => {}

  const postReady = (target: string) => {
    win.parent.postMessage(
      { v: EMBED_PROTOCOL_VERSION, type: 'bhookly:embed:ready', authenticated: deps.isAuthenticated(), role: deps.role() },
      target,
    )
  }

  let busy = false
  const onMessage = (e: MessageEvent) => {
    if (e.source !== win.parent) return
    if (!isIncoming(e.data)) return
    const m = e.data
    if (m.type === 'bhookly:embed:hello') {
      postReady(e.origin)
      return
    }
    if (m.type === 'bhookly:embed:logout') {
      deps.logout()
      postReady(e.origin)
      return
    }
    if (busy) return
    busy = true
    void deps
      .login(m.username, m.password)
      .then((r) => {
        win.parent.postMessage({ v: EMBED_PROTOCOL_VERSION, type: 'bhookly:embed:login-result', ...r }, e.origin)
        if (r.ok) deps.navigateHome()
      })
      .finally(() => {
        busy = false
      })
  }

  win.addEventListener('message', onMessage)
  // The ready ping carries nothing sensitive, and the frame cannot know its
  // parent's origin, so it is broadcast; replies to real requests go back to
  // the requester's origin only.
  postReady('*')
  return () => win.removeEventListener('message', onMessage)
}
